<template>
  <div class="data-workbench">
    <!-- 说明提示 -->
    <el-alert
      title="取数工作台 - 聊天触发取数主入口"
      type="info"
      :closable="false"
      show-icon
      style="margin-bottom: 20px"
    >
      <div slot="default">
        支持单票、多票、指数、板块、热点、第一本账分组、联动取数。
        <el-tag size="mini" type="warning">后端只做数据，不替代小七做分析</el-tag>
      </div>
    </el-alert>

    <el-row :gutter="20">
      <!-- 左侧：对象选择区 -->
      <el-col :span="16">
        <el-card>
          <div slot="header">
            <span><i class="el-icon-edit"></i> 分析请求参数</span>
          </div>

          <!-- 对象类型快捷按钮区 -->
          <div class="object-type-section">
            <div class="section-label">对象类型（点击切换）</div>
            <el-radio-group v-model="createForm.target_type" @change="onTargetTypeChange" size="small">
              <el-radio-button label="stock">单票</el-radio-button>
              <el-radio-button label="multi_stock">多票</el-radio-button>
              <el-radio-button label="index">指数</el-radio-button>
              <el-radio-button label="sector">板块</el-radio-button>
              <el-radio-button label="theme">题材</el-radio-button>
              <el-radio-button label="first_book_group">第一本账分组</el-radio-button>
              <el-radio-button label="batch_log">批次拉取</el-radio-button>
              <el-radio-button label="system_check">系统自检</el-radio-button>
            </el-radio-group>
          </div>

          <el-form :model="createForm" label-width="100px" size="small" style="margin-top:15px">
            <!-- 指数快捷按钮（当选择指数时显示） -->
            <el-form-item v-if="createForm.target_type === 'index'" label="常用指数">
              <div class="quick-btns">
                <el-button
                  v-for="idx in hotIndexes"
                  :key="idx.code"
                  :type="createForm.target_value === idx.code ? 'primary' : 'default'"
                  size="mini"
                  @click="createForm.target_value = idx.code"
                >{{ idx.name }}</el-button>
              </div>
            </el-form-item>

            <!-- 板块快捷标签 + 搜索框（当选择板块时显示） -->
            <el-form-item v-if="createForm.target_type === 'sector'" label="常用板块">
              <div class="quick-btns">
                <el-button
                  v-for="s in hotSectors"
                  :key="s"
                  :type="createForm.target_value === s ? 'primary' : 'default'"
                  size="mini"
                  @click="createForm.target_value = s"
                >{{ s }}</el-button>
              </div>
            </el-form-item>

            <!-- 题材快捷标签 -->
            <el-form-item v-if="createForm.target_type === 'theme'" label="常用题材">
              <div class="quick-btns">
                <el-button
                  v-for="t in hotThemes"
                  :key="t"
                  :type="createForm.target_value === t ? 'primary' : 'default'"
                  size="mini"
                  @click="createForm.target_value = t"
                >{{ t }}</el-button>
              </div>
            </el-form-item>

            <!-- 第一本账分组快捷按钮 -->
            <el-form-item v-if="createForm.target_type === 'first_book_group'" label="分组选择">
              <div class="quick-btns">
                <el-button
                  v-for="g in firstBookGroups"
                  :key="g.value"
                  :type="createForm.target_value === g.value ? 'primary' : 'default'"
                  size="mini"
                  @click="createForm.target_value = g.value"
                >{{ g.label }}</el-button>
              </div>
            </el-form-item>

            <!-- 目标值输入（搜索框模式） -->
            <el-form-item label="目标值">
              <StockCodeInput
                v-if="createForm.target_type === 'stock'"
                v-model="createForm.target_value"
                placeholder="如: 688347"
                style="width:100%"
              />
              <el-input
                v-else-if="createForm.target_type === 'multi_stock'"
                v-model="createForm.target_value"
                placeholder="688347.SZ,301171.SZ (逗号分隔)"
                style="width:100%"
              />
              <el-input
                v-else-if="createForm.target_type === 'index'"
                v-model="createForm.target_value"
                placeholder="000001.SH 或点击上方快捷按钮"
                style="width:100%"
              />
              <el-input
                v-else
                v-model="createForm.target_value"
                :placeholder="getTargetPlaceholder"
                style="width:100%"
              />
            </el-form-item>

            <!-- 意图类型（自动跟随对象类型） -->
            <el-form-item label="意图类型">
              <el-select v-model="createForm.intent_type" style="width:100%">
                <el-option label="单票分析" value="single_stock_analysis" />
                <el-option label="多票比较" value="multi_stock_analysis" />
                <el-option label="板块分析" value="sector_analysis" />
                <el-option label="批次分析" value="batch_log_analysis" />
                <el-option label="指数分析" value="index_analysis" />
                <el-option label="题材分析" value="theme_analysis" />
                <el-option label="第一本账分组分析" value="first_book_group_analysis" />
                <el-option label="系统自检" value="system_check" />
              </el-select>
            </el-form-item>

            <!-- 周期参数 -->
            <el-form-item label="周期参数">
              <el-checkbox-group v-model="createForm.periods">
                <el-checkbox label="monthly">月线</el-checkbox>
                <el-checkbox label="weekly">周线</el-checkbox>
                <el-checkbox label="daily">日线</el-checkbox>
                <el-checkbox label="60m">60分钟</el-checkbox>
                <el-checkbox label="15m">15分钟</el-checkbox>
              </el-checkbox-group>
            </el-form-item>

            <el-form-item label="数据范围">
              <el-select v-model="createForm.data_range" style="width:100%">
                <el-option label="1个月" value="1m" />
                <el-option label="3个月" value="3m" />
                <el-option label="6个月" value="6m" />
                <el-option label="1年" value="1y" />
              </el-select>
            </el-form-item>

            <el-form-item label="包含内容">
              <el-checkbox-group v-model="createForm.include">
                <el-checkbox label="market">行情数据</el-checkbox>
                <el-checkbox label="indicators">技术指标</el-checkbox>
                <el-checkbox label="announcements">公告摘要</el-checkbox>
                <el-checkbox label="news">消息摘要</el-checkbox>
                <el-checkbox label="first_book_mapping">第一本账映射</el-checkbox>
                <el-checkbox label="sector_mapping">板块映射</el-checkbox>
              </el-checkbox-group>
            </el-form-item>

            <el-form-item>
              <el-button type="primary" icon="el-icon-s-promotion" :loading="creating" @click="handleCreate">
                生成数据卡
              </el-button>
              <el-button icon="el-icon-refresh" @click="resetCreateForm">重置</el-button>
            </el-form-item>
          </el-form>
        </el-card>
      </el-col>

      <!-- 右侧：结果卡区 -->
      <el-col :span="8">
        <el-card>
          <div slot="header">
            <span><i class="el-icon-document"></i> 结果卡</span>
          </div>
          <div v-if="!createResult" class="empty-state">
            <i class="el-icon-s-promotion"></i>
            <p>创建分析请求后将显示 request_id 和 visible_stub</p>
          </div>
          <div v-else class="result-card">
            <el-alert
              :title="createResult.reused ? '幂等命中（已复用已有请求）' : '分析请求已创建'"
              :type="createResult.reused ? 'warning' : 'success'"
              :closable="false"
              show-icon
            />
            <el-descriptions :column="1" border size="small" style="margin-top:12px">
              <el-descriptions-item label="请求ID">
                <el-tag>{{ createResult.request_id }}</el-tag>
                <el-button type="text" size="mini" @click="copyToClipboard(createResult.request_id)">复制</el-button>
              </el-descriptions-item>
              <el-descriptions-item label="状态">{{ createResult.status }}</el-descriptions-item>
              <el-descriptions-item label="是否复用">{{ createResult.reused ? '是' : '否' }}</el-descriptions-item>
            </el-descriptions>
            <div style="margin-top:12px">
              <el-button size="small" type="primary" @click="viewRequestStatus(createResult.request_id)">查看状态</el-button>
              <el-button size="small" @click="viewFullChain(createResult.request_id)">全链路查询</el-button>
            </div>
          </div>
        </el-card>

        <!-- 最近请求区 -->
        <el-card style="margin-top:15px">
          <div slot="header">
            <span><i class="el-icon-time"></i> 最近请求</span>
            <el-button type="text" size="mini" style="float:right" @click="loadRecentRequests">刷新</el-button>
          </div>
          <el-table :data="recentRequests" size="mini" max-height="300" v-loading="recentLoading">
            <el-table-column prop="request_id" label="请求ID" min-width="180" show-overflow-tooltip />
            <el-table-column prop="target_type" label="类型" width="60" />
            <el-table-column prop="target_value" label="目标" width="100" show-overflow-tooltip />
            <el-table-column prop="status" label="状态" width="90">
              <template slot-scope="{row}">
                <el-tag :type="getStatusTagType(row.status)" size="mini">{{ getStatusLabel(row.status) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="70">
              <template slot-scope="{row}">
                <el-button type="text" size="mini" @click="createForm.target_value = row.target_value; createForm.target_type = row.target_type">引用</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-col>
    </el-row>

    <!-- 请求状态对话框 -->
    <el-dialog title="分析请求状态" :visible.sync="statusDialogVisible" width="700px">
      <div v-if="statusDetail">
        <el-descriptions :column="2" border size="small">
          <el-descriptions-item label="请求ID">{{ statusDetail.request_id }}</el-descriptions-item>
          <el-descriptions-item label="状态">
            <el-tag :type="getStatusTagType(statusDetail.status)">{{ getStatusLabel(statusDetail.status) }}</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="意图类型">{{ statusDetail.intent_type }}</el-descriptions-item>
          <el-descriptions-item label="目标类型">{{ statusDetail.target_type }}</el-descriptions-item>
          <el-descriptions-item label="目标值">{{ statusDetail.target_value }}</el-descriptions-item>
          <el-descriptions-item label="是否有载荷">{{ statusDetail.has_payload ? '是' : '否' }}</el-descriptions-item>
          <el-descriptions-item label="载荷日志ID" :span="2">{{ statusDetail.payload_log_id || '-' }}</el-descriptions-item>
        </el-descriptions>

        <h4 style="margin-top: 15px">事件历史</h4>
        <el-timeline>
          <el-timeline-item
            v-for="evt in statusDetail.events"
            :key="evt.event_id"
            :timestamp="evt.created_at"
            :type="evt.event_type === 'error' ? 'danger' : 'primary'"
          >
            {{ evt.message }}
          </el-timeline-item>
        </el-timeline>
      </div>
    </el-dialog>

    <!-- 全链路对话框 -->
    <el-dialog title="全链路查询" :visible.sync="chainDialogVisible" width="900px" top="5vh">
      <div v-if="chainData">
        <el-divider content-position="left">分析请求</el-divider>
        <el-descriptions :column="2" border size="small">
          <el-descriptions-item label="请求ID">{{ chainData.request.request_id }}</el-descriptions-item>
          <el-descriptions-item label="状态">{{ chainData.request.status }}</el-descriptions-item>
        </el-descriptions>

        <el-divider content-position="left">分析结论</el-divider>
        <div v-if="(chainData.conclusions || []).length === 0" style="color:#999">暂无结论</div>
        <div v-for="c in (chainData.conclusions || [])" :key="c.conclusion_id" style="margin-bottom:10px">
          <el-card shadow="hover">
            <div><strong>{{ c.conclusion_id }}</strong> | {{ c.version_type }}</div>
            <div style="margin-top:5px">{{ c.summary }}</div>
          </el-card>
        </div>

        <el-divider content-position="left">载荷信息</el-divider>
        <pre v-if="chainData.payload" style="background:#f5f7fa;padding:10px;border-radius:4px;max-height:400px;overflow:auto;font-size:12px">{{ JSON.stringify(chainData.payload.machine_payload, null, 2) }}</pre>
        <div v-else style="color:#999">暂无载荷</div>
      </div>
    </el-dialog>

    <!-- 分析结论弹窗（自动弹出，显示所有结论） -->
    <el-dialog
      title="分析结论"
      :visible.sync="conclusionsDialogVisible"
      width="900px"
      top="3vh"
      :close-on-click-modal="false"
      :close-on-press-escape="true"
    >
      <div v-loading="conclusionsDialogLoading">
        <el-alert
          v-if="currentAnalysisTarget"
          :title="`当前分析目标：${currentAnalysisTarget}`"
          type="info"
          :closable="false"
          show-icon
          style="margin-bottom:15px"
        />
        <div v-if="allConclusions.length === 0 && !conclusionsDialogLoading" style="text-align:center;padding:40px;color:#909399">
          <i class="el-icon-document" style="font-size:48px;display:block;margin-bottom:10px"></i>
          <p>暂无分析结论，请先创建分析请求并等待小七完成分析</p>
        </div>
        <div v-for="c in allConclusions" :key="c.conclusion_id" style="margin-bottom:12px">
          <el-card shadow="hover">
            <div slot="header" class="conclusion-card-header">
              <div class="conclusion-title-row">
                <el-tag size="small" :type="getVersionTagType(c.version_type)">{{ c.version_type }}</el-tag>
                <strong style="margin-left:8px">{{ c.ts_code }} {{ c.name }}</strong>
                <el-tag size="small" :type="c.confidence_level === '高' ? 'success' : c.confidence_level === '中' ? 'warning' : 'info'" style="margin-left:8px">
                  置信度: {{ c.confidence_level || '-' }}
                </el-tag>
                <el-tag v-if="c.model_name" type="warning" size="mini" style="margin-left:6px">{{ c.model_name }}</el-tag>
                <el-tag v-if="c.data_status" size="mini" :type="c.data_status === 'official' ? 'success' : 'warning'" style="margin-left:6px">
                  {{ c.data_status === 'official' ? '正式数据' : '第三方数据' }}
                </el-tag>
                <span style="margin-left:auto;font-size:12px;color:#909399">{{ c.created_at }}</span>
              </div>
            </div>
            <div class="conclusion-body">
              <div class="conclusion-summary">
                <strong>摘要：</strong>{{ c.summary }}
              </div>
              <div v-if="c.action_card" class="conclusion-action">
                <strong>动作卡：</strong>
                <span v-if="typeof c.action_card === 'object'">
                  <el-tag v-if="c.action_card.current_action" size="mini" style="margin-right:4px">当前: {{ c.action_card.current_action }}</el-tag>
                  <el-tag v-if="c.action_card.trigger_condition" size="mini" type="warning">触发: {{ c.action_card.trigger_condition }}</el-tag>
                </span>
                <span v-else>{{ c.action_card }}</span>
              </div>
              <div v-if="c.evidence_summary" class="conclusion-evidence">
                <strong>证据：</strong>{{ typeof c.evidence_summary === 'object' ? JSON.stringify(c.evidence_summary) : c.evidence_summary }}
              </div>
              <div class="conclusion-id" style="margin-top:8px;font-size:11px;color:#c0c4cc">
                ID: {{ c.conclusion_id }}
              </div>
            </div>
          </el-card>
        </div>
        <div v-if="allConclusions.length > 0" style="text-align:center;margin-top:15px">
          <el-pagination
            v-if="conclusionsTotal > 0"
            :current-page="conclusionsPage"
            :page-size="conclusionsPageSize"
            :total="conclusionsTotal"
            layout="total, prev, pager, next"
            size="small"
            @current-change="loadAllConclusions"
          />
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="conclusionsDialogVisible = false">关闭</el-button>
        <el-button type="primary" @click="refreshConclusions">刷新结论</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import StockCodeInput from '@/components/StockCodeInput.vue'
import { chatAnalysisApi } from '@/api'

export default {
  name: 'DataWorkbench',
  components: { StockCodeInput },
  data() {
    return {
      creating: false,
      createForm: {
        target_type: 'stock',
        intent_type: 'single_stock_analysis',
        target_value: '',
        periods: ['daily', '60m', '15m'],
        data_range: '3m',
        include: ['market', 'indicators', 'announcements']
      },
      createResult: null,

      // 快捷选项
      hotIndexes: [
        { code: '000001.SH', name: '上证指数' },
        { code: '399001.SZ', name: '深证成指' },
        { code: '399006.SZ', name: '创业板指' },
        { code: '000688.SH', name: '科创50' },
        { code: '399303.SZ', name: '国证2000' },
        { code: '000300.SH', name: '沪深300' }
      ],
      hotSectors: ['半导体', '算力', '光模块', 'PCB', '机器人', 'AI应用', '新能源汽车', '光伏'],
      hotThemes: ['机器人', 'AI应用', '半导体', '算力', '自动驾驶', '低空经济', '商业航天'],
      firstBookGroups: [
        { label: '今日优先', value: 'today_priority' },
        { label: '重点跟踪', value: 'key_watch' },
        { label: '正式区全部', value: 'formal_all' },
        { label: '临时区强候补', value: 'temp_strong_candidate' }
      ],

      // 最近请求
      recentRequests: [],
      recentLoading: false,

      // 对话框
      statusDialogVisible: false,
      statusDetail: null,
      chainDialogVisible: false,
      chainData: null,

      // 分析结论弹窗（自动弹出）
      conclusionsDialogVisible: false,
      conclusionsDialogLoading: false,
      currentAnalysisTarget: '',
      allConclusions: [],
      conclusionsTotal: 0,
      conclusionsPage: 1,
      conclusionsPageSize: 10
    }
  },
  computed: {
    getTargetPlaceholder() {
      const map = {
        stock: '688347.SH',
        index: '000001.SH',
        sector: '半导体',
        theme: 'AI应用',
        first_book_group: 'today_priority',
        batch_log: 'ANN-20260528-150000',
        system_check: 'system_health',
        multi_stock: '688347.SZ,301171.SZ'
      }
      return map[this.createForm.target_type] || '目标值'
    }
  },
  created() {
    this.loadRecentRequests()
  },
  methods: {
    onTargetTypeChange(val) {
      const intentMap = {
        stock: 'single_stock_analysis', multi_stock: 'multi_stock_analysis',
        index: 'index_analysis', sector: 'sector_analysis',
        theme: 'theme_analysis', first_book_group: 'first_book_group_analysis',
        batch_log: 'batch_log_analysis', system_check: 'system_check'
      }
      this.createForm.intent_type = intentMap[val] || 'single_stock_analysis'
      this.createForm.target_value = ''
    },

    async handleCreate() {
      if (!this.createForm.target_value) {
        this.$message.warning('请输入目标值')
        return
      }
      this.creating = true
      try {
        const safePeriods = Array.isArray(this.createForm.periods) && this.createForm.periods.length > 0
          ? this.createForm.periods : ['daily']
        const scopeParams = {
          periods: safePeriods,
          range: this.createForm.data_range || '3m',
          include: Array.isArray(this.createForm.include) ? this.createForm.include : [],
          include_announcement_summary: true
        }
        const params = {
          intent_type: this.createForm.intent_type,
          target_type: this.createForm.target_type,
          target_value: this.createForm.target_value,
          chat_thread_ref: '管理页面',
          scope_params_json: JSON.stringify(scopeParams)
        }
        const res = await chatAnalysisApi.createRequest(params)
        this.createResult = { request_id: res.request_id, status: res.status, reused: res.reused }
        if (res.reused) {
          this.$message.warning('幂等命中，已复用已有请求')
        } else {
          this.$message.success('分析请求已创建: ' + res.request_id)
        }
        this.loadRecentRequests()
        // 自动弹出结论弹窗，加载所有结论
        this.currentAnalysisTarget = `${this.createForm.target_type}: ${this.createForm.target_value}`
        this.openConclusionsDialog()
      } catch (e) {
        this.$message.error('创建失败: ' + (e.response && e.response.data && e.response.data.detail || e.message))
      } finally {
        this.creating = false
      }
    },

    resetCreateForm() {
      this.createForm = {
        target_type: 'stock', intent_type: 'single_stock_analysis',
        target_value: '', periods: ['daily', '60m', '15m'],
        data_range: '3m', include: ['market', 'indicators', 'announcements']
      }
      this.createResult = null
    },

    async loadRecentRequests() {
      this.recentLoading = true
      try {
        const res = await chatAnalysisApi.getHistory({ limit: 10 })
        this.recentRequests = Array.isArray(res.items) ? res.items : []
      } catch (e) {
        this.recentRequests = []
      } finally {
        this.recentLoading = false
      }
    },

    async viewRequestStatus(requestId) {
      try {
        const res = await chatAnalysisApi.getRequestStatus(requestId)
        this.statusDetail = { ...res, events: Array.isArray(res.events) ? res.events : [] }
        this.statusDialogVisible = true
      } catch (e) {
        this.$message.error('查询失败')
      }
    },

    async viewFullChain(requestId) {
      this.chainDialogVisible = true
      try {
        const res = await chatAnalysisApi.getFullChain(requestId)
        this.chainData = {
          request: res.request || {},
          conclusions: Array.isArray(res.conclusions) ? res.conclusions : [],
          payload: res.payload || null,
          tool_call_logs: Array.isArray(res.tool_call_logs) ? res.tool_call_logs : []
        }
      } catch (e) {
        this.$message.error('查询失败')
        this.chainData = { request: {}, conclusions: [], payload: null, tool_call_logs: [] }
      }
    },

    getStatusTagType(status) {
      const map = { created: 'info', data_fetching: 'warning', payload_ready: 'success', conclusion_saved: '', failed: 'danger', stale: 'info' }
      return map[status] || 'info'
    },
    getStatusLabel(status) {
      const map = { created: '已创建', data_fetching: '取数中', payload_ready: '载荷就绪', conclusion_saved: '结论已保存', failed: '失败', stale: '已过期' }
      return map[status] || status
    },
    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => this.$message.success('已复制')).catch(() => this.$message.warning('复制失败'))
    },

    // ============ 分析结论弹窗（显示所有结论） ============
    async openConclusionsDialog() {
      this.conclusionsDialogVisible = true
      this.conclusionsPage = 1
      await this.loadAllConclusions(1)
    },
    async loadAllConclusions(page) {
      this.conclusionsDialogLoading = true
      this.conclusionsPage = page || this.conclusionsPage
      try {
        const params = { limit: this.conclusionsPageSize, offset: (this.conclusionsPage - 1) * this.conclusionsPageSize }
        if (this.currentAnalysisTarget) {
          const parts = this.currentAnalysisTarget.split(': ')
          if (parts.length >= 2) {
            params.target_value = parts[1].trim()
            if (parts[0]) params.target_type = parts[0].trim()
          }
        }
        const res = await chatAnalysisApi.getRecentConclusions(params)
        this.allConclusions = Array.isArray(res.items) ? res.items : []
        this.conclusionsTotal = res.total || 0
      } catch (e) {
        console.error('加载结论失败:', e)
        this.allConclusions = []
        this.conclusionsTotal = 0
      } finally {
        this.conclusionsDialogLoading = false
      }
    },
    async refreshConclusions() {
      this.conclusionsPage = 1
      await this.loadAllConclusions(1)
      this.$message.success('结论已刷新')
    },
    getVersionTagType(versionType) {
      const map = {
        '盘前版': '', '盘中临时版': 'warning', '收盘真相版': 'danger',
        '盘后版': 'success', '次日验证版': 'info', '公告分析版': 'primary',
        '排障版': 'danger', '单票分析版': ''
      }
      return map[versionType] || 'info'
    }
  }
}
</script>

<style scoped>
.data-workbench { padding: 0; }
.empty-state { text-align: center; padding: 40px; color: #909399; }
.empty-state i { font-size: 48px; display: block; margin-bottom: 10px; }
.object-type-section { margin-bottom: 5px; }
.section-label { font-size: 13px; color: #606266; margin-bottom: 8px; font-weight: 500; }
.quick-btns { display: flex; flex-wrap: wrap; gap: 6px; }
.result-card { margin-top: 0; }

/* 结论弹窗卡片样式 */
.conclusion-card-header {
  padding: 0 !important;
}
.conclusion-title-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}
.conclusion-body {
  font-size: 14px;
}
.conclusion-summary {
  margin-bottom: 6px;
  line-height: 1.6;
}
.conclusion-action {
  margin-bottom: 4px;
  color: #606266;
}
.conclusion-evidence {
  color: #909399;
  font-size: 13px;
}
</style>
